import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import PageHeader from '../components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  BarChart3,
  AlertTriangle,
  CheckCircle2,
  Clock,
  TrendingUp,
  MapPin,
  Users,
  Activity,
  Shield,
  Lock,
  Download,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatSeconds = (s) => {
  if (!s || s === 0) return '0s';
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  return `${(s / 3600).toFixed(1)}h`;
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString([], {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const StatCard = ({ icon: Icon, label, value, accent, testId }) => (
  <div
    className={`bg-panel border ${accent || 'border-white/10'} rounded-sm p-4`}
    data-testid={testId}
  >
    <div className="flex items-center justify-between mb-2">
      <p className="font-mono text-[10px] text-muted-foreground uppercase tracking-wider">{label}</p>
      <Icon className="w-4 h-4 text-muted-foreground" />
    </div>
    <p className="font-chivo font-bold text-3xl text-white">{value}</p>
  </div>
);

const ReportsPage = () => {
  const { hasPermission, user } = useAuth();
  const canView = hasPermission('can_view_reports');

  const [days, setDays] = useState(30);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!canView) {
      setLoading(false);
      return;
    }
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, canView]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/reports/summary?days=${days}`);
      setReport(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = () => {
    if (!report) return;
    const lines = [];
    lines.push('Truth Church Security - Alert Report');
    lines.push(`Period (days),${report.period_days}`);
    lines.push('');
    lines.push('Totals');
    lines.push('Metric,Value');
    Object.entries(report.totals).forEach(([k, v]) => lines.push(`${k},${v}`));
    lines.push('');
    lines.push('Category Breakdown');
    lines.push('Category,Count');
    report.category_breakdown.forEach((c) => lines.push(`${c.label},${c.count}`));
    lines.push('');
    lines.push('Location Breakdown');
    lines.push('Location,Count');
    report.location_breakdown.forEach((l) => lines.push(`${l.location},${l.count}`));
    lines.push('');
    lines.push('Top Responders');
    lines.push('Name,Count');
    report.top_responders.forEach((r) => lines.push(`${r.name},${r.count}`));

    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tcs-report-${days}d-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Report exported');
  };

  if (!canView) {
    return (
      <div className="p-4 md:p-6" data-testid="reports-no-access">
        <PageHeader title="Reports" subtitle="Access restricted" />
        <Card className="bg-panel border-white/10 rounded-sm mt-6">
          <CardContent className="p-12 text-center space-y-4">
            <div className="w-16 h-16 rounded-sm bg-red-600/20 flex items-center justify-center mx-auto">
              <Lock className="w-8 h-8 text-red-400" />
            </div>
            <div>
              <p className="font-chivo font-bold text-lg text-white">Permission Required</p>
              <p className="font-inter text-sm text-muted-foreground mt-1">
                Your role <strong className="text-white">{user?.role?.replace(/_/g, ' ')}</strong> does not have the
                <code className="font-mono text-xs bg-black px-1.5 py-0.5 mx-1 rounded-sm">can_view_reports</code>
                permission. Contact your Security Head for access.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-4 md:p-6 space-y-6" data-testid="reports-loading">
        <PageHeader title="Reports" subtitle="Loading analytics..." />
        <div className="flex items-center justify-center py-20">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!report) return null;

  const maxTimeline = Math.max(...report.timeline.map((t) => t.count), 1);
  const maxCategory = Math.max(...report.category_breakdown.map((c) => c.count), 1);
  const maxLocation = Math.max(...report.location_breakdown.map((l) => l.count), 1);

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <PageHeader
          title="Reports"
          subtitle={`Alert analytics — last ${report.period_days} days`}
        />
        <div className="flex items-center gap-2">
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger
              className="bg-black border-white/20 rounded-sm w-36"
              data-testid="reports-period-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-panel border-white/10 rounded-sm">
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="180">Last 180 days</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={exportCSV}
            className="border-white/20 rounded-sm font-mono text-xs uppercase"
            data-testid="reports-export-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Top stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard
          icon={BarChart3}
          label="Total Alerts"
          value={report.totals.total}
          testId="stat-total"
        />
        <StatCard
          icon={AlertTriangle}
          label="Emergency"
          value={report.totals.emergency}
          accent="border-red-600/40"
          testId="stat-emergency"
        />
        <StatCard
          icon={Activity}
          label="Non-Emergency"
          value={report.totals.non_emergency}
          accent="border-orange-500/40"
          testId="stat-non-emergency"
        />
        <StatCard
          icon={Shield}
          label="Active"
          value={report.totals.active}
          accent="border-yellow-500/40"
          testId="stat-active"
        />
        <StatCard
          icon={CheckCircle2}
          label="Resolved"
          value={report.totals.resolved}
          accent="border-green-500/40"
          testId="stat-resolved"
        />
      </div>

      {/* Response Times */}
      <Card className="bg-panel border-white/10 rounded-sm">
        <CardHeader className="border-b border-white/10 pb-4">
          <CardTitle className="font-chivo font-bold text-lg uppercase tracking-tight text-white flex items-center gap-2">
            <Clock className="w-5 h-5 text-primary" />
            Response Times
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-surface rounded-sm p-3 text-center">
            <p className="font-mono text-[10px] text-muted-foreground uppercase">Average</p>
            <p className="font-chivo font-bold text-2xl text-white" data-testid="rt-avg">
              {formatSeconds(report.response_time.average_seconds)}
            </p>
          </div>
          <div className="bg-surface rounded-sm p-3 text-center">
            <p className="font-mono text-[10px] text-muted-foreground uppercase">Fastest</p>
            <p className="font-chivo font-bold text-2xl text-green-400">
              {formatSeconds(report.response_time.fastest_seconds)}
            </p>
          </div>
          <div className="bg-surface rounded-sm p-3 text-center">
            <p className="font-mono text-[10px] text-muted-foreground uppercase">Slowest</p>
            <p className="font-chivo font-bold text-2xl text-red-400">
              {formatSeconds(report.response_time.slowest_seconds)}
            </p>
          </div>
          <div className="bg-surface rounded-sm p-3 text-center">
            <p className="font-mono text-[10px] text-muted-foreground uppercase">Resolved</p>
            <p className="font-chivo font-bold text-2xl text-white">
              {report.response_time.resolved_count}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Two column section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Category breakdown */}
        <Card className="bg-panel border-white/10 rounded-sm">
          <CardHeader className="border-b border-white/10 pb-4">
            <CardTitle className="font-chivo font-bold text-lg uppercase tracking-tight text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              By Category
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-3">
            {report.category_breakdown.length === 0 && (
              <p className="font-mono text-xs text-muted-foreground text-center py-4">
                No alerts in this period
              </p>
            )}
            {report.category_breakdown.map((c) => (
              <div key={c.category} data-testid={`cat-row-${c.category}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-inter text-sm text-white">{c.label}</span>
                  <span className="font-mono text-xs text-muted-foreground">{c.count}</span>
                </div>
                <div className="h-2 bg-black rounded-sm overflow-hidden">
                  <div
                    className="h-full rounded-sm transition-all"
                    style={{
                      width: `${(c.count / maxCategory) * 100}%`,
                      backgroundColor: c.color,
                    }}
                  />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Location breakdown */}
        <Card className="bg-panel border-white/10 rounded-sm">
          <CardHeader className="border-b border-white/10 pb-4">
            <CardTitle className="font-chivo font-bold text-lg uppercase tracking-tight text-white flex items-center gap-2">
              <MapPin className="w-5 h-5 text-primary" />
              By Location
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-3 max-h-96 overflow-y-auto">
            {report.location_breakdown.length === 0 && (
              <p className="font-mono text-xs text-muted-foreground text-center py-4">
                No alerts in this period
              </p>
            )}
            {report.location_breakdown.map((l) => (
              <div key={l.location} data-testid={`loc-row-${l.location}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-inter text-sm text-white">{l.location}</span>
                  <span className="font-mono text-xs text-muted-foreground">{l.count}</span>
                </div>
                <div className="h-2 bg-black rounded-sm overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-sm transition-all"
                    style={{ width: `${(l.count / maxLocation) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Timeline */}
      {report.timeline.length > 0 && (
        <Card className="bg-panel border-white/10 rounded-sm">
          <CardHeader className="border-b border-white/10 pb-4">
            <CardTitle className="font-chivo font-bold text-lg uppercase tracking-tight text-white flex items-center gap-2">
              <Activity className="w-5 h-5 text-primary" />
              Timeline
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="flex items-end gap-1 h-32">
              {report.timeline.map((t) => (
                <div
                  key={t.date}
                  className="flex-1 flex flex-col items-center gap-1 group min-w-0"
                  title={`${t.date}: ${t.count} alerts`}
                >
                  <div
                    className="w-full bg-primary/70 hover:bg-primary rounded-sm transition-all"
                    style={{ height: `${(t.count / maxTimeline) * 100}%`, minHeight: '2px' }}
                  />
                  <span className="font-mono text-[8px] text-muted-foreground truncate w-full text-center">
                    {t.date.slice(5)}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top Responders + Recent */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="bg-panel border-white/10 rounded-sm">
          <CardHeader className="border-b border-white/10 pb-4">
            <CardTitle className="font-chivo font-bold text-lg uppercase tracking-tight text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              Top Reporters
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-2">
            {report.top_responders.length === 0 && (
              <p className="font-mono text-xs text-muted-foreground text-center py-4">No data</p>
            )}
            {report.top_responders.map((r, idx) => (
              <div
                key={r.name}
                className="flex items-center justify-between p-3 bg-surface rounded-sm"
                data-testid={`responder-${idx}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-sm bg-black flex items-center justify-center">
                    <span className="font-chivo font-bold text-xs text-white">{idx + 1}</span>
                  </div>
                  <span className="font-inter text-sm text-white">{r.name}</span>
                </div>
                <Badge variant="outline" className="font-mono text-xs border-primary/50 text-primary">
                  {r.count} alert{r.count === 1 ? '' : 's'}
                </Badge>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="bg-panel border-white/10 rounded-sm">
          <CardHeader className="border-b border-white/10 pb-4">
            <CardTitle className="font-chivo font-bold text-lg uppercase tracking-tight text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-primary" />
              Recent Alerts
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-2 max-h-96 overflow-y-auto">
            {report.recent_alerts.length === 0 && (
              <p className="font-mono text-xs text-muted-foreground text-center py-4">No alerts</p>
            )}
            {report.recent_alerts.map((a) => (
              <div
                key={a.id}
                className="p-3 bg-surface rounded-sm border-l-2"
                style={{ borderLeftColor: a.color }}
                data-testid={`recent-${a.id}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-inter font-semibold text-sm text-white">{a.label}</span>
                  <Badge
                    variant="outline"
                    className={`font-mono text-[10px] ${
                      a.status === 'resolved'
                        ? 'border-green-500/50 text-green-400'
                        : 'border-yellow-500/50 text-yellow-400'
                    }`}
                  >
                    {a.status}
                  </Badge>
                </div>
                <p className="font-mono text-xs text-muted-foreground truncate">
                  {a.location} • by {a.created_by_name}
                </p>
                <p className="font-mono text-[10px] text-muted-foreground/70 mt-1">
                  {formatDate(a.created_at)}
                </p>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ReportsPage;
